import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  getPP,
  getTC,
  getTCByUser,
  putAcceptTC,
} from 'api';
import { errorObj } from 'utils/handleError';
import { showErrorMessage } from 'utils/showMessage';
import useAuth from './useAuth';

/**
 * Hook che gestisce la logica di recupero e accettazione
 * di Termini&nbsp;&amp;&nbsp;Condizioni + Privacy&nbsp;Policy.
 * Il modale verrà mostrato **solo se almeno uno** dei due testi
 * contiene contenuto da visualizzare / firmare.
 */
const useTCPP = (isWorkspace = false) => {
  const { t } = useTranslation();
  const { platformData, getAccessToken, handleAcceptTC } = useAuth();

  /* ------------------------------------------------------------------
   * STATE
   * ----------------------------------------------------------------*/
  const [tcModal, setTcModal] = useState({ show: false, tc: null, pp: null });

  /* ------------------------------------------------------------------
   * FETCH T&C  -------------------------------------------------------*/
  const {
    isFetching: isTCFetching,
  } = useQuery({
    queryKey: ['getTC'],
    queryFn: () =>
      isWorkspace ? getTCByUser(getAccessToken()) : getTC(getAccessToken()),
    onSuccess: (res) => {
      const list =
        res.resData.data?.terms_and_conditions ?? res.resData.data?.data ?? [];
      if (list.length > 0) {
        setTcModal((prev) => ({
          ...prev,
          tc: {
            ...res.resData.data,
            data: list,
          },
        }));
      }
    },
    onError: (err) => {
      const errObj = errorObj(err);
      showErrorMessage(errObj?.resData?.errors?.[0]?.message || t('error'));
    },
    enabled: isWorkspace ? !platformData?.profile?.accepted_term_condition : true,
    refetchOnWindowFocus: false,
  });

  /* ------------------------------------------------------------------
   * FETCH Privacy Policy  -------------------------------------------*/
  const { isFetching: isPPFetching } = useQuery({
    queryKey: ['getPP'],
    queryFn: () => getPP(getAccessToken()),
    onSuccess: (res) => {
      const list =
        res.resData.data?.privacy_policies ?? res.resData.data?.data ?? [];
      if (list.length > 0) {
        setTcModal((prev) => ({
          ...prev,
          pp: {
            ...res.resData.data,
            data: list,
          },
        }));
      }
    },
    onError: (err) => {
      const errObj = errorObj(err);
      showErrorMessage(errObj?.resData?.errors?.[0]?.message || t('error'));
    },
    enabled: isWorkspace ? !platformData?.profile?.accepted_term_condition : true,
    refetchOnWindowFocus: false,
  });

  /* ------------------------------------------------------------------
   * DECIDE SE MOSTRARE IL MODALE  -----------------------------------*/
  useEffect(() => {
    const tcEmpty = !(tcModal.tc?.data?.length > 0);
    const ppEmpty = !(tcModal.pp?.data?.length > 0);

    setTcModal((prev) => ({ ...prev, show: !(tcEmpty && ppEmpty) }));
  }, [tcModal.tc, tcModal.pp]);

  /* ------------------------------------------------------------------
   * ACCEPT action  ---------------------------------------------------*/
  const acceptTC = useMutation({
    mutationKey: 'putAcceptTC',
    mutationFn: () => putAcceptTC(getAccessToken()),
    onSuccess: () => {
      handleAcceptTC();
      closeTcModal();
    },
    onError: (err) => {
      const errobj = errorObj(err);
      showErrorMessage(errobj?.resData?.errors?.[0]?.message || t('error'));
    },
  });

  /* ------------------------------------------------------------------
   * HELPERS  ---------------------------------------------------------*/
  function handleSubmitTcModal() {
    acceptTC.mutate();
  }
  function openTcModal() {
    setTcModal((prev) => ({ ...prev, show: true }));
  }
  function closeTcModal() {
    setTcModal((prev) => ({ ...prev, show: false }));
  }

  return {
    /* state & flags */
    tcModal,
    isTCFetching,
    isPPFetching,
    acceptTC,

    /* actions */
    handleSubmitTcModal,
    openTcModal,
    closeTcModal,
  };
};

export default useTCPP;
